import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { useAdminCollections } from '../../context/AdminCollections';
import { useStaticCollections } from '../../context/StaticCollections';
import { buildNominationExportData } from '../../utils/buildNominationExportData';
import { arrayToCSV } from '../../utils/arrayToCsv';
import { formatDraftNominationData } from '../../utils/formatDraftNominationData';
import { nominationColumns } from '../../utils/availableColumnsNominationDownload';
import { NOMINATION_STATUS } from '../../utils/constants';
import { devWarn, devError } from '../../utils/devLog';
import {
  createCandidateIdentifier,
  isSameCandidate,
} from '../../utils/candidateMatching';

const NominationCSVExport = () => {
  const {
    nominationArray,
    candidateArray,
    draftNominationArray,
    candidateStateArray,
    proposerSeconder: proposerSeconderArray,
    nominationStateArray,
    nominationSupporterArray,
    nominationDeclarationArray,
    nominationRefereeArray,
  } = useAdminCollections();

  const {
    electionTypes,
    nominationStatusTypes,
    nonAasRefereesArray,
    fellowsArray,
    sectionalCommitteesWithCmse,
    organisations,
    titlesTypes,
    stateTerritories,
    genderTypes,
    australianStatus,
    sectionalCommitteeHistoryArray,
  } = useStaticCollections();

  const [loading, setLoading] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [downloadFullNomination, setDownloadFullNomination] = useState(true);
  const [includeNonValidatedDraft, setIncludeNonValidatedDraft] =
    useState(true);
  const [dataStabilized, setDataStabilized] = useState(false);

  const isDataLoaded = useMemo(() => {
    const arraysReady =
      Array.isArray(nominationArray) &&
      nominationArray.length > 0 &&
      Array.isArray(candidateArray) &&
      candidateArray.length > 0 &&
      Array.isArray(draftNominationArray) &&
      Array.isArray(candidateStateArray) &&
      Array.isArray(proposerSeconderArray) &&
      Array.isArray(nominationStateArray) &&
      Array.isArray(nominationSupporterArray) &&
      Array.isArray(nominationDeclarationArray) &&
      Array.isArray(nominationRefereeArray) &&
      Array.isArray(nonAasRefereesArray) &&
      Array.isArray(fellowsArray) &&
      Array.isArray(sectionalCommitteeHistoryArray);

    const objectsReady =
      electionTypes &&
      typeof electionTypes === 'object' &&
      Object.keys(electionTypes).length > 0 &&
      nominationStatusTypes &&
      typeof nominationStatusTypes === 'object' &&
      Object.keys(nominationStatusTypes).length > 0 &&
      sectionalCommitteesWithCmse &&
      typeof sectionalCommitteesWithCmse === 'object' &&
      Object.keys(sectionalCommitteesWithCmse).length > 0 &&
      organisations &&
      typeof organisations === 'object' &&
      Object.keys(organisations).length > 0 &&
      titlesTypes &&
      typeof titlesTypes === 'object' &&
      Object.keys(titlesTypes).length > 0 &&
      stateTerritories &&
      typeof stateTerritories === 'object' &&
      Object.keys(stateTerritories).length > 0 &&
      genderTypes &&
      typeof genderTypes === 'object' &&
      Object.keys(genderTypes).length > 0 &&
      australianStatus &&
      typeof australianStatus === 'object' &&
      Object.keys(australianStatus).length > 0;

    const canProcessStatuses =
      nominationStatusTypes &&
      Object.entries(nominationStatusTypes).length > 0 &&
      Object.entries(nominationStatusTypes).every(
        ([key, value]) =>
          key && value && typeof key === 'string' && typeof value === 'string',
      );

    return arraysReady && objectsReady && canProcessStatuses;
  }, [
    nominationArray,
    candidateArray,
    draftNominationArray,
    candidateStateArray,
    proposerSeconderArray,
    nominationStateArray,
    nominationSupporterArray,
    nominationDeclarationArray,
    nominationRefereeArray,
    electionTypes,
    nominationStatusTypes,
    nonAasRefereesArray,
    fellowsArray,
    sectionalCommitteesWithCmse,
    organisations,
    titlesTypes,
    stateTerritories,
    genderTypes,
    australianStatus,
    sectionalCommitteeHistoryArray,
  ]);

  useEffect(() => {
    if (isDataLoaded) {
      setDataStabilized(false);
      const stabilizationTimer = setTimeout(() => {
        setDataStabilized(true);
      }, 5000);

      return () => clearTimeout(stabilizationTimer);
    }
    setDataStabilized(false);
  }, [isDataLoaded]);

  const isFullyReady = isDataLoaded && dataStabilized;

  const nominationStatuses = useMemo(() => {
    if (
      !nominationStatusTypes ||
      Object.keys(nominationStatusTypes).length === 0
    ) {
      return [];
    }
    return Object.entries(nominationStatusTypes).map(([value, label]) => ({
      value,
      label,
    }));
  }, [nominationStatusTypes]);

  const [selectedStatus, setSelectedStatus] = useState([]);

  useEffect(() => {
    if (
      isFullyReady &&
      nominationStatuses.length > 0 &&
      selectedStatus.length === 0
    ) {
      setSelectedStatus(nominationStatuses);
    }
  }, [isFullyReady, nominationStatuses, selectedStatus.length]);

  const columnOptions = useMemo(
    () => nominationColumns.map((col) => ({ value: col, label: col })),
    [],
  );

  const handleColumnChange = useCallback((selectedOptions) => {
    setSelectedColumns(selectedOptions.map((option) => option.value));
  }, []);

  const handleStatusChange = useCallback((selectedOptions) => {
    setSelectedStatus(selectedOptions);
  }, []);

  const handleDownloadFullNominationToggle = useCallback(() => {
    setDownloadFullNomination((prev) => {
      const newValue = !prev;
      if (newValue && isFullyReady) {
        setSelectedColumns(nominationColumns);
        setSelectedStatus(nominationStatuses);
        setIncludeNonValidatedDraft(true);
      } else {
        setSelectedColumns([]);
        setSelectedStatus([]);
      }
      return newValue;
    });
  }, [isFullyReady, nominationStatuses]);

  const generateCSVDownload = (data, columns = selectedColumns) => {
    const csv = `\uFEFF${arrayToCSV(data, columns)}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'nomination_data.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadNomination = async () => {
    if (!isFullyReady) {
      devWarn(
        'Cannot download nominations: essential data not yet loaded or stabilized',
      );
      return;
    }

    try {
      if (!nominationArray?.length || !candidateArray?.length) {
        throw new Error('Critical arrays are empty');
      }

      if (
        !nominationStatusTypes ||
        !Object.keys(nominationStatusTypes).length
      ) {
        throw new Error('Nomination status types not available');
      }

      if (nominationArray[0] && !nominationArray[0].attributes) {
        throw new Error('Nomination data structure is invalid');
      }

      setLoading(true);

      const columnsToUse = downloadFullNomination
        ? nominationColumns
        : selectedColumns;

      const draftCandidates = [];
      let draftNominationDataArray = [];

      if (includeNonValidatedDraft) {
        draftNominationDataArray = await Promise.all(
          draftNominationArray.map(async (draft) => {
            const draftData = await formatDraftNominationData(
              JSON.parse(draft.attributes.value),
              nominationStatusTypes,
              electionTypes,
              titlesTypes,
              stateTerritories,
              genderTypes,
              australianStatus,
              sectionalCommitteesWithCmse,
              sectionalCommitteeHistoryArray,
              nominationArray,
              candidateArray,
              nominationStateArray,
              candidateStateArray,
            );

            const formattedDraft = {
              id: draft.id,
              createdAt: draft.attributes.createdAt,
              updatedAt: draft.attributes.updatedAt,
              ...draftData,
            };

            draftCandidates.push(createCandidateIdentifier(draftData));

            return formattedDraft;
          }),
        );
      }

      const filteredNominations = nominationArray.filter((nomination) => {
        const statusMatches = selectedStatus.some(
          (status) =>
            status.value ===
            String(nomination.attributes.nominationStatusTypeId),
        );

        if (!statusMatches) return false;

        if (!includeNonValidatedDraft) return true;

        if (
          nomination.attributes.nominationStatusTypeId ===
          NOMINATION_STATUS.DRAFT
        ) {
          const { candidateId } = nomination.attributes;
          const candidate = candidateArray?.find((c) => c.id === candidateId);

          if (candidate) {
            const regularCandidateInfo = createCandidateIdentifier(
              candidate.attributes,
            );

            const hasDraft = draftCandidates.some((draftCandidate) =>
              isSameCandidate(regularCandidateInfo, draftCandidate),
            );

            if (hasDraft) return false;
          }
        }

        return true;
      });

      const nominationDataArray = await Promise.all(
        filteredNominations.map(async (nomination) => {
          const data = buildNominationExportData(nomination, {
            nominationStatusTypes,
            electionTypes,
            organisations,
            titlesTypes,
            stateTerritories,
            genderTypes,
            australianStatus,
            sectionalCommitteesWithCmse,
            sectionalCommitteeHistoryArray,
            candidateArray,
            candidateStateArray,
            proposerSeconderArray,
            nominationStateArray,
            nominationSupporterArray,
            nominationDeclarationArray,
            nominationRefereeArray,
            fellowsArray,
            nonAasRefereesArray,
          });

          return data;
        }),
      );

      const finalData = includeNonValidatedDraft
        ? [...draftNominationDataArray, ...nominationDataArray]
        : nominationDataArray;

      generateCSVDownload(finalData, columnsToUse);
    } catch (error) {
      devError('Error downloading nominations:', error);
      if (
        error.message.includes('empty') ||
        error.message.includes('invalid') ||
        error.message.includes('not available')
      ) {
        setDataStabilized(false);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="col-md-6 mb-3">
      <div className="card h-100 d-flex flex-column justify-content-center admin-card">
        <div className="card-header">
          <h4 className="card-title">Download nomination</h4>
        </div>
        <div className="card-body flex-grow-1">
          <div className="form-check form-switch mt-3">
            <label className="form-check-label" htmlFor="toggleDownload">
              Download full nomination
            </label>
            <input
              className="form-check-input"
              type="checkbox"
              id="toggleDownload"
              checked={downloadFullNomination}
              onChange={handleDownloadFullNominationToggle}
            />
          </div>
          {!downloadFullNomination && (
            <div className="mt-3">
              <div className="form-check form-switch mt-3">
                <label className="form-check-label" htmlFor="toggleDownload">
                  Include non validated draft
                </label>
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="toggleDownload"
                  checked={includeNonValidatedDraft}
                  onChange={() => setIncludeNonValidatedDraft((prev) => !prev)}
                />
              </div>

              <label className="form-label" htmlFor="columnFilter">
                Select nominations to include
              </label>
              <Select
                closeMenuOnSelect={false}
                isMulti
                options={nominationStatuses}
                value={nominationStatuses.filter((option) =>
                  selectedStatus.some(
                    (status) => status.value === option.value,
                  ),
                )}
                onChange={handleStatusChange}
                className="mb-3"
              />

              <label className="form-label" htmlFor="columnFilter">
                Select columns to include in CSV
              </label>
              <Select
                closeMenuOnSelect={false}
                isMulti
                options={columnOptions}
                value={columnOptions.filter((option) =>
                  selectedColumns.includes(option.value),
                )}
                onChange={handleColumnChange}
                className="mb-3"
              />
            </div>
          )}
        </div>
        <div className="mt-auto p-3 d-flex justify-content-end align-items-center flex-grow-1">
          <button
            type="button"
            className="btn btn-success standard-width-button"
            onClick={downloadNomination}
            disabled={loading || !isFullyReady}
            aria-label="Download CSV"
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            title="Press to download CSV"
          >
            <i className="bi bi-download" />{' '}
            {loading ? 'Processing...' : 'Download'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NominationCSVExport;
